import { isDefined } from '@sgme/fp';
import type { TenorFieldData } from 'components/shared/TenorField/TenorField';
import { getTimeSpanFormattedValue } from 'components/shared/TimeSpanField/TimeSpanField.helper';
import type { SPIntl } from 'contexts/intl/intlContext';
import type { Locale } from 'date-fns';
import type { IntlKey } from 'locales';
import type {
  CustomizableTenorFieldMetadata,
  FieldMetadata,
  ListFieldMetadata,
  LockablePercentFieldData,
  SpecificFieldData,
} from 'models/forms/fields';
import { MultiSelectFieldMetadata } from 'models/forms/fields/MultiSelectFieldMetadata';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { Underlying } from 'models/underlying';
import type { FormDataStateMap } from 'state/formData/formData.model';
import { camelCaseToWords } from 'utils/string';

export function getCellValuesByFieldType(
  metaData: Record<string, FieldMetadata>,
  fieldValues: Record<string, unknown>,
  intl: SPIntl,
  locale: Locale,
): FormDataStateMap {
  const result: any = {};

  for (const key in fieldValues) {
    if (metaData.hasOwnProperty(key) && !metaData[key].isHidden && isDefined(fieldValues[key])) {
      result[key] = getCellValueByFieldType(metaData[key], fieldValues[key], intl, locale);
    }
  }

  return result;
}

function getCellValueByFieldType(
  cellMetadata: FieldMetadata,
  cellValue: unknown,
  { formatMessage, formatDisplayedDate }: SPIntl,
  locale: Locale,
) {
  switch (cellMetadata.type) {
    case 'LockablePercentField':
      const value = cellValue as LockablePercentFieldData;
      return Number.isFinite(value.value) ? `${value.value} %` : '';
    case 'PercentField':
      return Number.isFinite(cellValue) ? `${cellValue} ${cellMetadata.unit ?? ''}` : '';
    case 'NumberField':
    case 'AmountField':
      return Number.isFinite(cellValue) ? cellValue : '';
    case 'ToggleField':
      return formatMessage({
        id: `${cellValue ? 'enabled' : 'disabled'}` as IntlKey,
      });
    case 'CustomizableTenorField':
      return getCustomizableTenorFieldValue(cellValue, formatMessage, cellMetadata);
    case 'UnderlyingsField':
    case 'MultipleUnderlyingsField':
      return getMutlipleUnderlyingsFieldValue(cellValue);
    case 'ClientListField':
      const client = cellValue as ISalesCounterpart;
      return `${client.label} (${client.id})`;
    case 'DateField':
      return formatDisplayedDate(cellValue as string);
    case 'MultipleUnitsField':
      return (cellValue as SpecificFieldData<typeof cellMetadata.type>).value;
    case 'ListField':
      return getListFieldValue(cellMetadata, cellValue, formatMessage);
    case 'SpotAndGreeksField':
      return cellValue;
    case 'TimeSpanField':
      return getTimeSpanFormattedValue(cellValue as string, locale);
    case 'CurrencyListField':
    case 'ExternalQuoteIdField':
    default:
      return cellValue?.toString() ?? '';
  }
}

function getListFieldValue(
  cellMetadata: ListFieldMetadata | MultiSelectFieldMetadata,
  cellValue: unknown,
  formatMessage: SPIntl['formatMessage'],
) {
  const metadata = cellMetadata.availableItems.find(x => x.value === cellValue);

  return formatMessage({
    id: `field.${metadata?.translationKey ?? metadata?.label}` as IntlKey,
    defaultMessage: camelCaseToWords(metadata?.label),
  });
}

function getCustomizableTenorFieldValue(
  cellValue: unknown,
  formatMessage: SPIntl['formatMessage'],
  cellMetadata: CustomizableTenorFieldMetadata,
) {
  const tenorValue = cellValue as TenorFieldData;

  const unit = formatMessage({
    id: `form.${cellMetadata.translationKey}.option.${tenorValue.currentValue.unit}` as any,
  });
  return `${tenorValue.currentValue.value} ${unit}`;
}

function getMutlipleUnderlyingsFieldValue(cellValue: unknown): string {
  const underlyings = cellValue as ReadonlyArray<Underlying>;
  let result = '';
  underlyings.forEach(item => {
    if (item.asset === 'Equity' || item.asset === 'Commodity' || item.asset === 'Rate' || item.asset === 'Forex') {
      switch (item.idType) {
        case 'Bloomberg':
          result += item.bloombergTickerCode + ', ';
          break;
        case 'Reuters':
          result += item.reutersCode + ', ';
          break;
        case 'Eliot':
          result += item.eliotCode + ', ';
          break;
        default:
          result += item.id + ', ';
      }
    }
  });

  return result.slice(0, -2);
}
